// src/components/Modal.tsx
import { useEffect, useId, useRef, type ReactElement, type ReactNode } from 'react';

// Centered modal dialog primitive. Shares JobDrawer's accessibility mechanics
// (backdrop click + Esc to close, focus-into-panel + restore, role="dialog"
// aria-modal) but centers the panel instead of docking it to the side. Used by
// the new-job form and the complete/fail/cancel confirmation dialogs (A1EE-338).
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}): ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Esc to close. onClose is referenced directly so the dep list is honest.
  // Registered in the CAPTURE phase (and stopping propagation on Escape) so a
  // Modal nested inside JobDrawer closes the modal WITHOUT also tripping
  // JobDrawer's bubble-phase window keydown listener, which would navigate to
  // '/'. Capture runs window→target before the bubble phase, so this listener
  // fires first and halts the event before it reaches the drawer's handler.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Move focus into the dialog on open; restore it on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl outline-none"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-2 py-1 text-sm text-slate-600 hover:border-slate-400"
          >
            Close
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}
